import { HttpEvent, HttpEventType } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ServiceResponse } from '@nts/std';
import { LogService } from '@nts/std/utility';
import { ExpenseFileCollectionItemViewModel } from 'src/app/receipt-long-op/view-models/expense-file.collection-item-view-model';
import type { ReceiptLongOpOrchestratorViewModel } from 'src/app/receipt-long-op/view-models/receipt-long-op.orchestrator-view-model';

interface FullScreenPreviewState {
  currentImageModalSrc: SafeResourceUrl;
  currentImageModalBlob: Blob;
  currentImageModalName: string;
  currentItemInFullScreen: ExpenseFileCollectionItemViewModel;
}

export class ExpenseFileUtils {

  static areAllFilesUploaded(files: ExpenseFileCollectionItemViewModel[]): boolean {
    return files.every((file) =>
      (file.progress == null || file.progress === 100) && file.fileErrors.length === 0
    );
  }

  static openFileFullScreen(
    state: FullScreenPreviewState,
    currentImageModalSrc: SafeResourceUrl,
    currentImageModalBlob: Blob,
    currentImageModalName: string,
    item: ExpenseFileCollectionItemViewModel
  ): void {
    state.currentImageModalSrc = currentImageModalSrc;
    state.currentImageModalBlob = currentImageModalBlob;
    state.currentImageModalName = currentImageModalName;
    state.currentItemInFullScreen = item;
  }

  static navigateFullScreenImage(
    files: ExpenseFileCollectionItemViewModel[],
    currentItem: ExpenseFileCollectionItemViewModel,
    direction: 'next' | 'previous',
    state: FullScreenPreviewState,
    showOnlyFirst = false,
    hideFirst = false
  ): void {
    const filesWithPreview = files
      .slice(hideFirst ? 1 : 0, showOnlyFirst ? 1 : undefined)
      .filter((file) => file.filePreviewUrl != null);
    if (filesWithPreview.length === 0) {
      return;
    }

    const currentIndex = filesWithPreview.indexOf(currentItem);
    if (currentIndex === -1) {
      return;
    }

    const nextIndex = direction === 'next'
      ? (currentIndex < filesWithPreview.length - 1 ? currentIndex + 1 : 0)
      : (currentIndex > 0 ? currentIndex - 1 : filesWithPreview.length - 1);
    const nextItem = filesWithPreview[nextIndex];

    ExpenseFileUtils.openFileFullScreen(
      state,
      nextItem.filePreviewUrl,
      nextItem.fileObject ?? nextItem.fileBlob,
      nextItem.originalFileName.value,
      nextItem
    );
  }

  static viewExpenseFile(
    item: ExpenseFileCollectionItemViewModel,
    previewState: FullScreenPreviewState,
    orchestrator: ReceiptLongOpOrchestratorViewModel,
    sanitizer: DomSanitizer,
    detectChanges: () => void
  ): void {
    if (item.filePreviewUrl != null) {
      ExpenseFileUtils.openFileFullScreen(
        previewState,
        item.filePreviewUrl,
        item.fileObject ?? item.fileBlob,
        item.originalFileName.value,
        item
      );
      detectChanges();
      return;
    }

    const identity = item.domainModel?.attachmentIdentity;
    if (identity != null) {
      orchestrator.apiClient.downloadFile(identity).subscribe({
        next: (httpEvent: HttpEvent<Blob>) => {
          if (httpEvent?.type === HttpEventType.Response) {
            const blob = httpEvent.body;
            const extension = item.originalFileName.value.split('.').pop()?.toLowerCase();

            switch (extension) {
              case 'png':
              case 'jpg':
              case 'gif':
              case 'bmp':
              case 'jpeg': {
                const imageSource = sanitizer.bypassSecurityTrustUrl(window.URL.createObjectURL(blob));
                ExpenseFileUtils.openFileFullScreen(
                  previewState,
                  imageSource,
                  blob,
                  item.originalFileName.value,
                  item
                );
                detectChanges();
                break;
              }
              default: {
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.download = item.originalFileName.value;
                link.click();
              }
            }
          } else if (httpEvent instanceof ServiceResponse) {
            orchestrator.toastMessageService.showToastsFromResponse(httpEvent);
          }
        },
        error: (error) => {
          LogService.warn('ERRORE downloadFile', error);
        }
      });
      return;
    }

    if (item?.fileObject?.type?.startsWith('image') && item.filePreviewUrl != null) {
      ExpenseFileUtils.openFileFullScreen(
        previewState,
        item.filePreviewUrl,
        item.fileObject,
        item.originalFileName.value,
        item
      );
    } else {
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(item.fileObject);
      link.download = item.originalFileName.value;
      link.click();
    }
  }
}
